import type { JSX } from 'react';
import { cx } from './cx';

/**
 * The determinate bar under the percentage on the dashboard — Material 3's
 * `LinearProgressIndicator(progress = { … })` as Home draws it.
 *
 * `value` is a fraction in 0..1, the same shape Compose takes, and it is clamped
 * rather than trusted: `checked / total` is a division by a link count that is
 * zero before the list arrives, and a `NaN` or a `140%` width would be a bar
 * that visibly lies about a run in progress.
 *
 * **The 250ms is CSS, not JavaScript.** Android animates the value itself with
 * `animateFloatAsState(tween(250))`; here `.progress-track__fill` carries
 * `transition: width 250ms ease-out`, which gets the identical easing out of the
 * compositor without a state update per frame — and, because it is a CSS
 * transition, it is already inside the `prefers-reduced-motion` block at the
 * bottom of components.css. Driving it from React would put that gate back on
 * this component and let the two halves of Motion.kt's rule drift apart.
 */
export function LinearProgress({
  value,
  label,
  className,
}: {
  value: number;
  label?: string;
  className?: string;
}): JSX.Element {
  const fraction = Number.isFinite(value) ? Math.min(Math.max(value, 0), 1) : 0;
  const percent = Math.round(fraction * 100);

  return (
    <div
      className={cx('progress-track', className)}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      aria-label={label}
    >
      {/*
       * The one inline style in this component, and it is data rather than
       * design: the width *is* the measurement. Everything about how the bar
       * looks — the track colour, the fill colour, the radius, the transition —
       * lives in components.css where the rest of the theme is.
       */}
      <div className="progress-track__fill" style={{ width: `${fraction * 100}%` }} />
    </div>
  );
}

/**
 * The indeterminate circular indicator — Material 3's
 * `CircularProgressIndicator`, as Settings' busy dialog and the loading state of
 * a `NeonButton` use it.
 *
 * `prominent` is the 24px `IconSize.prominent` the busy dialog asks for; the
 * default is the 20px `IconSize.row` that sits inline with a label.
 *
 * The colour comes from `currentColor`, so a caller tints it by tinting its
 * container — which is how Android passes `color = NeonCyan` at the one call
 * site that wants something other than the surrounding text colour, and here is
 * a `.c-accent` in `className`.
 *
 * **Always decorative.** Both places it appears already say what is happening in
 * words that the spinner would only repeat: the busy dialog has a title and a
 * body, and a loading `NeonButton` keeps its label as the button's accessible
 * name and carries `aria-busy`. Announcing the spinner as well reads the same
 * state twice.
 *
 * Note this is the one thing in the app that keeps moving under
 * `prefers-reduced-motion` — it is slowed, not stopped (see the block at the end
 * of components.css). Motion.kt's rule is about *decorative* motion; a busy
 * indicator is information, and a frozen one says "this app has hung", which is
 * the exact misreading it exists to prevent.
 */
export function Spinner({
  prominent = false,
  className,
}: {
  prominent?: boolean;
  className?: string;
}): JSX.Element {
  return (
    <span
      className={cx('spinner', prominent && 'spinner--prominent', className)}
      aria-hidden="true"
    />
  );
}
